import * as O from 'fp-ts/Option';
import * as E from 'fp-ts/Either';
import { pipe } from 'fp-ts/lib/function';
import { zonedTimeToUtc } from 'date-fns-tz';
import { isValid } from 'date-fns';
import {
  UpdateMatchInput,
  CreateMatchInput,
  Match,
  Team,
} from '../../generated/graphql';

import { isNil } from '../../utils/isNil';
import { sequenceTOption } from '../../function';
import { MatchFields } from './MatchForm';

const makeMatchDate = (date: string, time: string, timeZone: string) =>
  pipe(
    zonedTimeToUtc(`${date}T${time}`, timeZone),
    E.right,
    E.filterOrElse(isValid, () => 'Invalid Date'),
    E.map((d) => d.toISOString()),
    E.mapLeft((message) => ({ matchTime: message, matchDate: ' ' })),
  );

export const sanitizeCreateMatchArgs = (
  values: MatchFields,
): E.Either<Record<string, string>, CreateMatchInput> => {
  return pipe(
    makeMatchDate(values.matchDate, values.matchTime, values.timeZone),
    E.map((playedAt) => ({
      playedAt,
      meetAt: pipe(
        makeMatchDate(values.matchDate, values.meetTime, values.timeZone),
        E.getOrElseW(() => undefined),
      ),
      opponent: values.opponent,
      teamId: values.teamId,
      atHome: values.atHome === 'yes',
      excludedFromStats: values.excludedFromStats === 'yes',
      timeZone: values.timeZone,
    })),
  );
};

export const sanitizeUpdateMatchArgs = (
  values: MatchFields,
): UpdateMatchInput => {
  return {
    playedAt: pipe(
      makeMatchDate(values.matchDate, values.matchTime, values.timeZone),
      E.getOrElseW(() => undefined),
    ),
    meetAt: pipe(
      makeMatchDate(values.matchDate, values.meetTime, values.timeZone),
      E.getOrElseW(() => undefined),
    ),
    opponent: values.opponent,
    atHome: values.atHome === 'yes',
    excludedFromStats: values.excludedFromStats === 'yes',
    timeZone: values.timeZone,
  };
};

export const mapMatchErrors = (errors: Record<string, string>) => {
  if (errors.teamId === 'already exists for this time') {
    // this error relates to the unique `played_at` constraint
    return { ...errors, matchTime: 'A match already exists for this time' };
  }

  return errors;
};

export type MatchPartsForTeamNames = Pick<Match, 'opponent' | 'atHome'> & {
  team: Pick<Team, 'name'>;
};
/**
 * For a given match, returns the team names in a 2-element
 * tuple where the first element is the home team and the 2nd
 * element is the away team.
 *
 * @param match
 */
export const getOrderedTeamNames = ({
  atHome,
  opponent: opponentName,
  team: { name: teamName },
}: MatchPartsForTeamNames): [string, string] => {
  return atHome ? [teamName, opponentName] : [opponentName, teamName];
};

/**
 * For a given match, returns the score in a 2-element
 * tuple where the first element is the home score and the 2nd
 * element is the away score.
 *
 * @param match
 */
export const getOrderedTeamScore = ({
  atHome,
  goalsFor,
  goalsAgainst,
}: Pick<Match, 'goalsFor' | 'goalsAgainst' | 'atHome'>) => {
  return pipe(
    sequenceTOption(O.fromNullable(goalsFor), O.fromNullable(goalsAgainst)),
    O.map(([goalsFor, goalsAgainst]) =>
      atHome
        ? ([goalsFor, goalsAgainst] as const)
        : ([goalsAgainst, goalsFor] as const),
    ),
  );
};

type MatchResult = 'W' | 'L' | 'D' | '?';
export const getMatchResult = (
  match: Pick<Match, 'goalsFor' | 'goalsAgainst'>,
) => {
  return pipe(
    [
      O.fromNullable(match.goalsFor),
      O.fromNullable(match.goalsAgainst),
    ] as const,
    (scores) => sequenceTOption(...scores),
    O.map(([gf, ga]) => (gf > ga ? 'W' : gf < ga ? 'L' : 'D')),
    O.getOrElse<MatchResult>(() => '?'),
  );
};

export const resultBgClass: Record<MatchResult, string> = {
  '?': 'bg-primary',
  W: 'bg-green-500',
  L: 'bg-red-300',
  D: 'bg-grey-400',
};

/**
 * Does the given match have a score set?
 *
 * @param match
 * @returns boolean
 */
export const hasScore = (match: Pick<Match, 'goalsFor' | 'goalsAgainst'>) =>
  !isNil(match.goalsFor) && !isNil(match.goalsAgainst);

export const isTeamMember = (team: {
  isOwner: boolean;
  player?: { id: string } | null;
}) => team.isOwner || !isNil(team.player);
